import { createFileRoute, Link, Outlet, useChildMatches } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { can } from '../../lib/roles';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import {
  listPsychometricTests,
  createPsychometricTest,
  getMyTestResults,
  type PsychometricTestDto,
  type TestResultDto,
} from '../../lib/psychometricTests';
import { listUsers, type UserDto } from '../../lib/users';
import {
  Brain,
  Plus,
  Clock,
  HelpCircle,
  CheckCircle2,
  Circle,
  FlaskConical,
  Pencil,
  TrendingUp,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const Route = createFileRoute('/kinerja/psychometrics')({
  component: PsychometricsPage,
});

function PsychometricsPage() {
  const childMatches = useChildMatches();
  // If a child route (take/edit) is active, render it instead of the list
  if (childMatches.length > 0) {
    return (
      <ProtectedRoute minimumRole="employee">
        <Outlet />
      </ProtectedRoute>
    );
  }
  return (
    <ProtectedRoute minimumRole="employee">
      <PsychometricsContent />
    </ProtectedRoute>
  );
}

const TEST_TYPE_LABELS: Record<string, string> = {
  mbti: 'MBTI',
  big_five: 'Big Five',
  disc: 'DISC',
  custom: 'Custom',
  personality: 'Personality',
  cognitive: 'Cognitive',
  leadership: 'Leadership',
};

const TEST_TYPE_COLORS: Record<string, string> = {
  mbti: 'bg-purple-100 text-purple-700',
  big_five: 'bg-blue-100 text-blue-700',
  disc: 'bg-orange-100 text-orange-700',
  cognitive: 'bg-cyan-100 text-cyan-700',
  leadership: 'bg-emerald-100 text-emerald-700',
  personality: 'bg-pink-100 text-pink-700',
  custom: 'bg-gray-100 text-gray-600',
};

function PsychometricsContent() {
  const { user } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;
  const userId = user?.id ?? '';
  const canManage = can(user?.role, 'manage:kinerja');

  const [tests, setTests] = useState<PsychometricTestDto[]>([]);
  const [loading, setLoading] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [newTest, setNewTest] = useState({ name: '', description: '', type: 'custom', estimatedTime: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Results
  const [results, setResults] = useState<TestResultDto[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsUserId, setResultsUserId] = useState<string>('');
  const [employees, setEmployees] = useState<UserDto[]>([]);
  const [empsLoaded, setEmpsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listPsychometricTests(companyId).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (res.success && res.data) setTests(res.data.tests);
    });
    return () => { cancelled = true; };
  }, [companyId]);

  // Load results for the selected user (self by default)
  useEffect(() => {
    if (!userId) return;
    const targetId = resultsUserId || userId;
    let cancelled = false;
    setResultsLoading(true);
    getMyTestResults(targetId, companyId).then((res) => {
      if (cancelled) return;
      setResultsLoading(false);
      if (res.success && res.data) setResults(res.data.results);
    });
    return () => { cancelled = true; };
  }, [userId, resultsUserId]);

  // Load employees once for manager selector
  useEffect(() => {
    if (!canManage || empsLoaded) return;
    setEmpsLoaded(true);
    let cancelled = false;
    listUsers(companyId, { isActive: true }).then((res) => {
      if (cancelled) return;
      if (res.success && res.data) setEmployees(res.data.users);
    });
    return () => { cancelled = true; };
  }, [canManage, companyId, empsLoaded]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    setCreateError('');
    const res = await createPsychometricTest(
      {
        companyId,
        name: newTest.name,
        description: newTest.description,
        type: newTest.type,
        estimatedTime: newTest.estimatedTime ? Number(newTest.estimatedTime) : undefined,
        questions: [],
      },
    );
    setCreating(false);
    if (res.success && res.data) {
      toast.success('Psychometric test created');
      setTests((prev) => [...prev, res.data!.test]);
      setShowCreate(false);
      setNewTest({ name: '', description: '', type: 'custom', estimatedTime: '' });
    } else {
      toast.error(res.error ?? 'Failed to create test');
      setCreateError(res.error ?? 'Failed to create test');
    }
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Psychometric Tests</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            MBTI, Big Five, DISC and custom assessments
          </p>
        </div>
        {canManage && (
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4 mr-1.5" />
            New Test
          </Button>
        )}
      </div>

      {/* Test grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-40 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      ) : tests.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-16 text-center">
            <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center mb-4">
              <Brain className="w-6 h-6 text-primary" />
            </div>
            <p className="text-sm font-medium text-foreground">No psychometric tests yet</p>
            <p className="text-xs text-muted-foreground mt-1 mb-4">
              {canManage
                ? 'Create the first test to start measuring your team'
                : 'Your HR team has not set up any tests yet'}
            </p>
            {canManage && (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                <Plus className="w-4 h-4 mr-1.5" />
                New Test
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tests.map((t) => {
            const typeColorClass = TEST_TYPE_COLORS[t.type] ?? 'bg-gray-100 text-gray-600';
            return (
              <Card key={t.id} className="flex flex-col">
                <CardContent className="flex flex-col gap-3 flex-1 py-5">
                  {/* Top row */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center shrink-0">
                        <Brain className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground leading-tight">
                        {t.name}
                      </h3>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${typeColorClass}`}>
                      {TEST_TYPE_LABELS[t.type] ?? t.type}
                    </span>
                  </div>

                  {/* Description */}
                  {t.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2">{t.description}</p>
                  )}

                  {/* Meta row */}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <HelpCircle className="w-3 h-3" />
                      {t.questions.length} q{t.questions.length !== 1 ? 's' : ''}
                    </span>
                    {t.estimatedTime && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {t.estimatedTime} min
                      </span>
                    )}
                    <span className="ml-auto flex items-center gap-1">
                      {t.isActive ? (
                        <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                      ) : (
                        <Circle className="w-3 h-3 text-muted-foreground" />
                      )}
                      {t.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-auto pt-3 border-t">
                    <Link
                      to={'/kinerja/psychometrics/$testId/take' as any}
                      params={{ testId: t.id } as any}
                      className="flex-1"
                    >
                      <Button size="sm" className="w-full">
                        <FlaskConical className="w-3.5 h-3.5 mr-1.5" />
                        Take Test
                      </Button>
                    </Link>
                    {canManage && (
                      <Link
                        to={'/kinerja/psychometrics/$testId/edit' as any}
                        params={{ testId: t.id } as any}
                      >
                        <Button size="sm" variant="outline" className="shrink-0">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      </Link>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* My Results section */}
      <Separator />
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            <h2 className="text-base font-semibold text-foreground">
              {resultsUserId && resultsUserId !== userId ? 'Employee Results' : 'My Results'}
            </h2>
          </div>
          {canManage && employees.length > 0 && (
            <div className="flex items-center gap-2">
              <Users className="w-3.5 h-3.5 text-muted-foreground" />
              <Select
                value={resultsUserId || userId}
                onValueChange={(v) => setResultsUserId(v === userId ? '' : v)}
              >
                <SelectTrigger className="h-8 text-xs w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={userId}>— My results —</SelectItem>
                  {employees.filter((e) => e.id !== userId).map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          Psychometric scores contribute to your potential rating on the 9-Box grid and inform your IDP development targets.
        </p>

        {resultsLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2].map((i) => <div key={i} className="h-28 bg-muted rounded-xl animate-pulse" />)}
          </div>
        ) : results.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-8 text-center">
            <TrendingUp className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No test results yet.</p>
            <p className="text-xs text-muted-foreground mt-1">Complete a test above to see your scores here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((r) => {
              const testName = tests.find((t) => t.id === r.testId)?.name ?? 'Unknown Test';
              const score = typeof r.totalScore === 'number'
                ? r.totalScore
                : Number(r.totalScore) || 0;
              return (
                <Card key={r.id} className="flex flex-col">
                  <CardContent className="py-4 px-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-foreground leading-tight">{testName}</p>
                      <Badge variant="secondary" className="shrink-0 text-xs">
                        {new Date(r.completedAt).toLocaleDateString()}
                      </Badge>
                    </div>
                    {/* Score ring */}
                    <div className="flex items-center gap-3">
                      <div className="relative w-14 h-14 shrink-0">
                        <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
                          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f1f5f9" strokeWidth="3" />
                          <circle
                            cx="18"
                            cy="18"
                            r="15.9"
                            fill="none"
                            stroke="hsl(var(--brand-500, 209 100% 50%))"
                            strokeWidth="3"
                            strokeDasharray={`${score} ${100 - score}`}
                            strokeLinecap="round"
                            className="text-primary"
                            style={{ stroke: 'hsl(var(--primary))' }}
                          />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-foreground">
                          {Math.round(score)}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Total Score</p>
                        <p className="text-lg font-bold text-foreground">{Math.round(score)}<span className="text-xs font-normal text-muted-foreground">/100</span></p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New Psychometric Test</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="psy-name">Title</Label>
              <Input
                id="psy-name"
                required
                placeholder="e.g. Big Five Personality Assessment"
                value={newTest.name}
                onChange={(e) => setNewTest((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="psy-type">Type</Label>
              <Select
                value={newTest.type}
                onValueChange={(v) => setNewTest((f) => ({ ...f, type: v }))}
              >
                <SelectTrigger id="psy-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TEST_TYPE_LABELS).map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="psy-desc">Description (optional)</Label>
              <Textarea
                id="psy-desc"
                rows={2}
                placeholder="What does this test measure?"
                value={newTest.description}
                onChange={(e) => setNewTest((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="psy-time">Estimated time (minutes, optional)</Label>
              <Input
                id="psy-time"
                type="number"
                min={1}
                placeholder="e.g. 20"
                value={newTest.estimatedTime}
                onChange={(e) => setNewTest((f) => ({ ...f, estimatedTime: e.target.value }))}
              />
            </div>
            {createError && <p className="text-xs text-destructive">{createError}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? 'Creating…' : 'Create Test'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
